import { NavLink } from 'react-router-dom';
import { useRole } from '@/hooks/useRole';
import { selectBottomNavItems } from './bottom-nav-items';

/**
 * Wave 3 P2d (2026-05-22): role-aware mobile bottom-nav.
 *
 * Pre-fix: hardcoded 5 items (Home/Capture/Expenses/Reports/More) shown
 * unconditionally to all roles. Pure-manager users (entity_membership.role =
 * 'manager') saw owner-side items even though backend role-gates would 403
 * them. Per CLAUDE.md design: manager (level 5) sits BELOW viewer (10);
 * pure managers should NOT see owner-side surfaces.
 *
 * Post-fix: branches by `isManager` from useRole hook.
 *   - Pure manager → shows /manager/* tabs (Approvals + Team + Profile)
 *   - Owner / admin / bookkeeper / accountant / viewer → shows the 5
 *     owner-side items (existing behavior preserved)
 *   - Employee → route-redirected to /employee/* via AppShell guard;
 *     /employee/* uses EmployeeLayout which has its own bottom nav
 *
 * Owner / admin visiting /manager/* preserve owner nav (their role
 * is still 'owner' or 'admin' — only entity-membership.role determines
 * what they see). This is intentional per the role-gate matrix.
 *
 * Also added (NS4 R6 parity): safe-area-inset-bottom for iOS notched
 * iPhones. Previously the owner-side BottomNav clipped under the home
 * indicator on iPhone X+; EmployeeLayout already honored it.
 */

export function BottomNav({ onOpenNav }: { onOpenNav?: () => void } = {}) {
  const { role, hasMinRole } = useRole();
  const items = selectBottomNavItems(role, hasMinRole);
  const tabCls = (active: boolean) =>
    `flex flex-col items-center justify-center gap-1 px-3 py-1 rounded-[var(--radius-md)] transition-colors min-w-[64px] ${
      active ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
    }`;
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Primary mobile navigation"
    >
      <div className="flex items-center justify-around h-16 px-2">
        {items.map(({ to, icon: Icon, label }) => {
          // The "More" shortcut opens the left drawer (= the desktop sidebar)
          // instead of a separate /more page, so the menu is identical on mobile
          // and desktop and is reachable from the top even if this bar is covered
          // by the phone browser's bottom toolbar.
          if (to === '/more' && onOpenNav) {
            return (
              <button
                key={to + label}
                type="button"
                onClick={onOpenNav}
                aria-label="Open menu"
                className={tabCls(false)}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[11px] font-medium">Menu</span>
              </button>
            );
          }
          return (
            <NavLink key={to + label} to={to} end={to === '/'} className={({ isActive }) => tabCls(isActive)}>
              <Icon className="w-5 h-5" />
              <span className="text-[11px] font-medium">{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
